import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { AlertTriangle, Plus, Clock, CheckCircle, Search as SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLORS = {
  open: "#F59E0B", assigned: "#D4AF37", investigating: "#D4AF37",
  action_required: "#ef4444", resolved: "#556B2F", closed: "#71717A",
};

export default function IssueManagement() {
  const { API } = useAuth();
  const [issues, setIssues] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ property_id: "", issue_type: "legal", priority: "medium", title: "", description: "" });

  const fetchData = useCallback(async () => {
    try {
      const [iRes, pRes] = await Promise.all([
        fetch(`${API}/issues`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (iRes.ok) setIssues(await iRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createIssue = async () => {
    try {
      await fetch(`${API}/issues`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      });
      setShowCreate(false);
      setForm({ property_id: "", issue_type: "legal", priority: "medium", title: "", description: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const openCount = issues.filter(i => !["resolved", "closed"].includes(i.status)).length;

  return (
    <div data-testid="issue-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Issue Management</h2>
          <p className="text-white/40 text-sm font-sans">{openCount} open issues | {issues.length} total</p>
        </div>
        <Button data-testid="create-issue-btn" onClick={() => setShowCreate(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> Report Issue
        </Button>
      </div>

      {issues.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <AlertTriangle className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No issues reported. Report a property issue to get professional assistance.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map(issue => {
            const color = STATUS_COLORS[issue.status] || "#71717A";
            return (
              <div key={issue.issue_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" style={{ color }} />
                      <h4 className="font-sans font-medium text-white text-sm">{issue.title}</h4>
                    </div>
                    <p className="text-white/40 text-xs font-sans mt-0.5">{issue.property?.name || issue.property_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize" style={{ color, backgroundColor: color + "15" }}>{issue.status.replace(/_/g, " ")}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm font-sans ${issue.priority === "critical" || issue.priority === "high" ? "bg-red-400/10 text-red-400" : "bg-white/5 text-white/40"}`}>{issue.priority}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans bg-white/5 text-white/30 capitalize">{issue.issue_type}</span>
                  </div>
                </div>
                {issue.description && <p className="text-white/50 text-xs font-sans mb-3">{issue.description}</p>}
                {/* Mini timeline */}
                {issue.timeline?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {issue.timeline.map((t, i) => (
                      <span key={i} className="text-[9px] font-sans text-white/20 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        {t.stage.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Report Issue</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="issue-property-select" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Issue Type</label>
                <Select value={form.issue_type} onValueChange={v => setForm({ ...form, issue_type: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="regulatory">Regulatory</SelectItem>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Title</label>
              <Input data-testid="issue-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brief issue title..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detailed description..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-issue-btn" onClick={createIssue} disabled={!form.property_id || !form.title} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">Submit Issue</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
